import { useCallback, useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  DoDisturbOutlined as DoDisturbOutlinedIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  DataObjectOutlined as DataObjectOutlinedIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../BrandOnBoarding/api";
import { AuthContext } from "../../../context/auth-context";
import useHTMLReportHistory from "../../BrandAudit/HTMLReportTab/useHTMLReportHistory";
import CustomizedTables from "../../../Component/UI/Table/Table";
import { StyledTableCell, StyledTableRow } from "../../../Component/UI/Table/Table";
import tabClasses from "../../BrandAudit/HTMLReportTab/HTMLReportTab.module.css";

const PAGE_SIZE = 5;

function getTableHeaders(entityType) {
  const entityHeaderName =
    String(entityType || "").toLowerCase() === "product" ? "Product" : "Brand";
  return [
    { id: "entity", name: entityHeaderName, sorting: false },
    { id: "category", name: "Category", sorting: false },
    { id: "reportType", name: "Report Type", sorting: false },
    { id: "date", name: "Date", sorting: false },
    { id: "status", name: "Status", sorting: false },
    { id: "actions", name: "Actions", sorting: false },
  ];
}

function formatReportType(reportType) {
  const value = String(reportType || "Brief").toLowerCase();
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDate(dateString) {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatDateTime(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function ReportTypeChip({ reportType }) {
  const label = formatReportType(reportType);
  const isFull = label === "Full";
  return (
    <Chip
      label={label}
      size="small"
      color={isFull ? "info" : "default"}
      variant={isFull ? "filled" : "outlined"}
    />
  );
}

function StatusChip({ status }) {
  if (status === "completed") return <Chip label="Completed" color="success" size="small" />;
  if (status === "failed") return <Chip label="Failed" color="error" size="small" />;
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <CircularProgress size={12} thickness={5} />
      <Chip label="In Progress" color="warning" size="small" />
    </Box>
  );
}

function ReportJobRow({ item, onViewInline, onViewInNewTab, onDownload, onDelete, inlineViewLoading }) {
  const [downloading, setDownloading] = useState(false);
  const [viewingNew, setViewingNew] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isCompleted = item.status === "completed";
  const isInProgress = item.status === "in-progress" || item.status === "pending";

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const isCategoryOnly = item.selections?.queryType === "category";
      await onDownload(item.jobId, item.transactionId, {
        module: "conversations",
        brandName: isCategoryOnly ? undefined : item.selections?.entity,
        categoryName: item.selections?.category,
      });
    }
    finally { setDownloading(false); }
  };

  const handleOpenNew = async () => {
    setViewingNew(true);
    try { await onViewInNewTab(item.jobId); }
    finally { setViewingNew(false); }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await onDelete(item.jobId);
      setDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <StyledTableRow>
        <StyledTableCell>{item.selections?.entity || "—"}</StyledTableCell>
        <StyledTableCell>{item.selections?.category || "—"}</StyledTableCell>
        <StyledTableCell align="center">
          <ReportTypeChip reportType={item.selections?.reportType} />
        </StyledTableCell>
        <StyledTableCell title={formatDateTime(item.createdAt)}>
          {formatDate(item.createdAt)}
        </StyledTableCell>
        <StyledTableCell><StatusChip status={item.status} /></StyledTableCell>
        <StyledTableCell>
          <Box display="flex" alignItems="center" gap={0.5}>
            {isCompleted && (
              <>
                <Tooltip title="View inline">
                  <IconButton size="small" className={tabClasses.actionIcon} onClick={() => onViewInline(item.jobId)} disabled={inlineViewLoading}>
                    {inlineViewLoading ? <CircularProgress size={14} /> : <VisibilityOutlinedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in new tab">
                  <IconButton size="small" className={tabClasses.actionIcon} onClick={handleOpenNew} disabled={viewingNew}>
                    {viewingNew ? <CircularProgress size={14} /> : <OpenInNewIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download HTML">
                  <IconButton size="small" className={tabClasses.actionIcon} onClick={handleDownload} disabled={downloading}>
                    {downloading ? <CircularProgress size={14} /> : <DownloadOutlinedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={isInProgress ? "Cancel generation" : "Delete report"}>
              <IconButton size="small" className={tabClasses.actionIconDanger} onClick={() => setDeleteDialog(true)}>
                {isInProgress ? <DoDisturbOutlinedIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </StyledTableCell>
      </StyledTableRow>

      <Dialog open={deleteDialog} onClose={() => !deleting && setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{isInProgress ? "Cancel Generation?" : "Delete Report?"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isInProgress
              ? "This will stop the report generation and remove it from history."
              : "This will permanently remove the report. This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleting}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <CircularProgress size={16} /> : isInProgress ? "Cancel Generation" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const REPORT_TYPE_OPTIONS = ["Full", "Brief"];

function ReportTypeSelectModal({ open, onClose, onConfirm, submitting }) {
  const [selected, setSelected] = useState(["Brief"]);
  const [error, setError] = useState(false);

  const handleToggle = (value) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    setError(false);
  };

  const handleConfirm = () => {
    if (selected.length === 0) {
      setError(true);
      return;
    }
    onConfirm(selected);
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Select Report Type</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Choose the report type(s) to generate. Selecting both will generate the reports in parallel.
        </Typography>
        <FormGroup>
          {REPORT_TYPE_OPTIONS.map((option) => (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  checked={selected.includes(option)}
                  onChange={() => handleToggle(option)}
                />
              }
              label={option}
            />
          ))}
        </FormGroup>
        {error && <FormHelperText error>Select at least one report type.</FormHelperText>}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
          {submitting ? <CircularProgress size={16} color="inherit" /> : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ConversationsReportingModal({
  open,
  onClose,
  onViewInlineOpen,
  userId,
  transactionId,
  conversationsData,
}) {
  const { user } = useContext(AuthContext);

  const [generating, setGenerating] = useState(false);
  const [downloadingJson, setDownloadingJson] = useState(false);
  const [page, setPage] = useState(1);
  const [inlineLoadingJobId, setInlineLoadingJobId] = useState(null);
  const [reportTypeModalOpen, setReportTypeModalOpen] = useState(false);

  const entityName = conversationsData?.metadata?.entity || "";

  const {
    jobs,
    loading: historyLoading,
    addJob,
    downloadReport,
    viewReport,
    cancelJob,
    refreshHistory,
  } = useHTMLReportHistory({ userId, transactionId, types: "conversations_html" });

  const entityType = conversationsData?.metadata?.type || jobs?.[0]?.selections?.type || "brand";
  const TABLE_HEADERS = getTableHeaders(entityType);

  const totalPages = Math.ceil(jobs.length / PAGE_SIZE);
  const pagedJobs = jobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const inProgressCount = jobs.filter((j) => j.status === "in-progress" || j.status === "pending").length;
  const hasJobs = jobs.length > 0;

  // ── Download JSON ──────────────────────────────────────────────────────────

  const handleDownloadJson = useCallback(() => {
    if (!conversationsData) return;
    setDownloadingJson(true);
    try {
      const filename = `${entityName || "Conversations"}_${new Date().toISOString().split("T")[0]}.json`;
      const blob = new Blob([JSON.stringify(conversationsData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download JSON");
    } finally {
      setDownloadingJson(false);
    }
  }, [conversationsData, entityName]);

  // ── Generate HTML report ───────────────────────────────────────────────────

  const generateForReportType = useCallback(async (reportType) => {
    const selections = { ...(conversationsData?.metadata || {}), reportType };
    const res = await api.post("/llm-reporting/generateConversationsHTMLReport", {
      userId,
      transactionId,
      conversationsData,
      selections,
    });
    const { jobId } = res.data;
    if (!jobId) throw new Error("No job ID received from server");
    addJob(jobId, selections);
  }, [userId, transactionId, conversationsData, addJob]);

  const handleReportTypeConfirm = useCallback(async (reportTypes) => {
    setGenerating(true);
    try {
      const results = await Promise.allSettled(reportTypes.map((rt) => generateForReportType(rt)));
      const failures = results.filter((r) => r.status === "rejected");
      if (failures.length === 0) {
        setPage(1);
        setReportTypeModalOpen(false);
        toast.info("Report generation started. Track the status in the table below.", { autoClose: 5000 });
      } else if (failures.length < results.length) {
        setPage(1);
        setReportTypeModalOpen(false);
        toast.warn("Some report generations failed to start. Please check and retry if needed.");
      } else {
        const msg = failures[0].reason?.response?.data?.error || failures[0].reason?.message || "Failed to start report generation";
        toast.error(msg);
      }
    } finally {
      setGenerating(false);
    }
  }, [generateForReportType]);

  const initiateGeneration = useCallback(() => {
    setReportTypeModalOpen(true);
  }, []);

  // ── Inline viewer ──────────────────────────────────────────────────────────

  const handleViewInline = useCallback(async (jobId) => {
    setInlineLoadingJobId(jobId);
    try {
      const response = await api.get(`/llm-reporting/download/${jobId}`, { responseType: "text" });
      const html = response.data || "";

      const onSaveUpdate = async (newHtml) => {
        await api.patch(`/llm-reporting/html-report/${jobId}`, { html: newHtml, userId, transactionId });
        await refreshHistory();
      };

      const onSaveNew = async (newHtml) => {
        await api.post("/llm-reporting/html-report", { html: newHtml, userId, transactionId, sourceJobId: jobId });
        await refreshHistory();
        setPage(1);
      };

      onViewInlineOpen({ jobId, html, transactionId, userId, onSaveUpdate, onSaveNew });
      onClose();
    } catch {
      toast.error("Failed to load report. Please try again.");
    } finally {
      setInlineLoadingJobId(null);
    }
  }, [onViewInlineOpen, onClose, userId, transactionId, refreshHistory]);

  // ── View in new tab ────────────────────────────────────────────────────────

  const handleViewInNewTab = useCallback(async (jobId) => {
    try {
      await viewReport(jobId);
    } catch (err) {
      if (err?.message === "popup_blocked") {
        toast.warn("Popup blocked. Please allow popups for this site.");
      } else {
        toast.error("Failed to open report.");
      }
    }
  }, [viewReport]);

  const hasData = !!conversationsData?.data;

  return (
    <>
    <ReportTypeSelectModal
      open={reportTypeModalOpen}
      onClose={() => setReportTypeModalOpen(false)}
      onConfirm={handleReportTypeConfirm}
      submitting={generating}
    />
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { mx: 2, my: 3, maxHeight: "calc(100% - 48px)" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" component="span">
            Reporting{entityName ? ` — ${entityName}` : ""}
          </Typography>
          {inProgressCount > 0 && (
            <Box className={tabClasses.inProgressBadge}>
              <CircularProgress size={10} thickness={5} />
              <span>{inProgressCount} generating…</span>
            </Box>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={() => { refreshHistory(); setPage(1); }}
                disabled={historyLoading}
                className={tabClasses.actionIcon}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {hasJobs && (
          <Alert severity="info" sx={{ borderRadius: 0, borderBottom: "1px solid #e5e7eb", fontSize: "13px" }}>
            HTML reports capture a full snapshot of this conversations analysis. Generate new versions or view previous ones below.
          </Alert>
        )}

        {historyLoading && !hasJobs ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !hasJobs ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} sx={{ py: 6 }}>
            <Typography variant="body2" color="text.secondary">
              No reports generated yet for this conversations analysis.
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                onClick={initiateGeneration}
                disabled={generating || !hasData}
                startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
              >
                {generating ? "Generating…" : "Generate Report"}
              </Button>

              {user.is_json_export_enabled &&
                <Button
                  variant="outlined"
                  onClick={handleDownloadJson}
                  disabled={downloadingJson || !hasData}
                  startIcon={downloadingJson ? <CircularProgress size={16} color="inherit" /> : <DataObjectOutlinedIcon />}
                >
                  {downloadingJson ? "Downloading…" : "Download JSON"}
                </Button>}

            </Box>
          </Box>
        ) : (
          <CustomizedTables
            headers={TABLE_HEADERS}
            data={pagedJobs}
            elevation={0}
            size="medium"
            render={(item) => (
              <ReportJobRow
                key={item.jobId}
                item={item}
                onViewInline={handleViewInline}
                onViewInNewTab={handleViewInNewTab}
                onDownload={downloadReport}
                onDelete={cancelJob}
                inlineViewLoading={inlineLoadingJobId === item.jobId}
              />
            )}
          />
        )}
      </DialogContent>

      {hasJobs && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderTop: "1px solid #e5e7eb",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography className={tabClasses.resultsText}>
              Showing{" "}
              <strong>{Math.min((page - 1) * PAGE_SIZE + 1, jobs.length)}</strong>{" "}
              to <strong>{Math.min(page * PAGE_SIZE, jobs.length)}</strong> of{" "}
              <strong>{jobs.length}</strong>
            </Typography>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                shape="rounded"
                size="small"
                className={tabClasses.pagination}
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>

            {user.is_json_export_enabled &&
              <Button
                variant="outlined"
                size="small"
                startIcon={downloadingJson ? <CircularProgress size={14} color="inherit" /> : <DataObjectOutlinedIcon />}
                disabled={downloadingJson || !hasData}
                onClick={handleDownloadJson}
              >
                {downloadingJson ? "Downloading…" : "Download JSON"}
              </Button>}
            <Button
              variant="outlined"
              size="small"
              startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
              disabled={generating || !hasData}
              onClick={initiateGeneration}
            >
              {generating ? "Generating…" : "Generate New Version"}
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
    </>
  );
}
